import type {
  DocumentIntelligenceRoute,
  DocumentIntelligenceScreen,
} from "./types";

export const DOCUMENT_INTELLIGENCE_BASE = "/document-intelligence";

const staticRoutes: Record<string, DocumentIntelligenceScreen> = {
  "": "assistant",
  assistant: "assistant",
  drafts: "draft",
  tables: "table",
  sources: "sources",
  playbooks: "playbooks",
  workflows: "workflows",
  projects: "projects",
  history: "history",
  settings: "settings",
};

export function parseDocumentIntelligenceRoute(
  pathname = window.location.pathname,
  search = window.location.search
): DocumentIntelligenceRoute {
  const normalized = pathname.replace(/\/+$/, "");
  const suffix = normalized.startsWith(DOCUMENT_INTELLIGENCE_BASE)
    ? normalized.slice(DOCUMENT_INTELLIGENCE_BASE.length).replace(/^\/+/, "")
    : "";
  const parts = suffix.split("/").filter(Boolean).map(decodeURIComponent);

  const workspaceId = new URLSearchParams(search).get("workspace") || undefined;

  if (parts.length === 0) return { screen: "assistant", ...(workspaceId ? { workspaceId } : {}) };

  if (parts[0] === "threads" && parts[1]) {
    return { screen: "thread", id: parts[1] };
  }
  if (parts[0] === "drafts") {
    return { screen: "draft", id: parts[1], ...(workspaceId ? { workspaceId } : {}) };
  }
  if (parts[0] === "tables") {
    return { screen: "table", id: parts[1], ...(workspaceId ? { workspaceId } : {}) };
  }
  if (parts[0] === "playbooks" && parts[1] === "new") {
    return { screen: "playbook-create" };
  }
  if (parts[0] === "playbooks" && parts[1]) {
    return { screen: "playbook", id: parts[1] };
  }
  if (parts[0] === "workflows" && parts[1] === "new") {
    return { screen: "workflow-builder" };
  }
  if (parts[0] === "workflows" && parts[1]) {
    return { screen: "workflow-builder", id: parts[1] };
  }
  if (parts[0] === "runs" && parts[1]) {
    return { screen: "workflow-run", id: parts[1] };
  }
  if (parts[0] === "projects" && parts[1] === "new") {
    return { screen: "project-new" };
  }
  if (parts[0] === "projects" && parts[1]) {
    return { screen: "project", id: parts[1] };
  }

  return { screen: staticRoutes[parts[0]] || "assistant", ...(workspaceId ? { workspaceId } : {}) };
}

export function documentIntelligencePath(route: DocumentIntelligenceRoute): string {
  const encodedId = route.id ? encodeURIComponent(route.id) : "";
  const workspace = route.workspaceId
    ? `?workspace=${encodeURIComponent(route.workspaceId)}`
    : "";
  switch (route.screen) {
    case "assistant":
      return `${DOCUMENT_INTELLIGENCE_BASE}/assistant${workspace}`;
    case "thread":
      return `${DOCUMENT_INTELLIGENCE_BASE}/threads/${encodedId}`;
    case "draft":
      return encodedId
        ? `${DOCUMENT_INTELLIGENCE_BASE}/drafts/${encodedId}`
        : `${DOCUMENT_INTELLIGENCE_BASE}/drafts${workspace}`;
    case "table":
      return encodedId
        ? `${DOCUMENT_INTELLIGENCE_BASE}/tables/${encodedId}`
        : `${DOCUMENT_INTELLIGENCE_BASE}/tables${workspace}`;
    case "sources":
      return `${DOCUMENT_INTELLIGENCE_BASE}/sources`;
    case "playbooks":
      return `${DOCUMENT_INTELLIGENCE_BASE}/playbooks`;
    case "playbook-create":
      return `${DOCUMENT_INTELLIGENCE_BASE}/playbooks/new`;
    case "playbook":
      return `${DOCUMENT_INTELLIGENCE_BASE}/playbooks/${encodedId}`;
    case "workflows":
      return `${DOCUMENT_INTELLIGENCE_BASE}/workflows`;
    case "workflow-builder":
      return encodedId
        ? `${DOCUMENT_INTELLIGENCE_BASE}/workflows/${encodedId}`
        : `${DOCUMENT_INTELLIGENCE_BASE}/workflows/new`;
    case "workflow-run":
      return `${DOCUMENT_INTELLIGENCE_BASE}/runs/${encodedId}`;
    case "projects":
      return `${DOCUMENT_INTELLIGENCE_BASE}/projects`;
    case "project-new":
      return `${DOCUMENT_INTELLIGENCE_BASE}/projects/new`;
    case "project":
      return `${DOCUMENT_INTELLIGENCE_BASE}/projects/${encodedId}`;
    case "history":
      return `${DOCUMENT_INTELLIGENCE_BASE}/history`;
    case "settings":
      return `${DOCUMENT_INTELLIGENCE_BASE}/settings`;
    default:
      return `${DOCUMENT_INTELLIGENCE_BASE}/assistant`;
  }
}

export function navigateDocumentIntelligence(
  route: DocumentIntelligenceRoute,
  options: { replace?: boolean } = {}
): void {
  const path = documentIntelligencePath(route);
  if (options.replace) window.history.replaceState({ docint: true }, "", path);
  else window.history.pushState({ docint: true }, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
